import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Kanban,
  ArrowRight,
  User,
  Clock,
  AlertTriangle,
  RefreshCw,
  Plus,
  GripVertical,
  Building2,
  Zap,
  RotateCcw,
  TrendingUp,
} from "lucide-react";

const COLUMN_CONFIG = {
  NEW_SIGNALS: { label: "New Signals", color: "bg-blue-500", textColor: "text-blue-400", icon: Zap },
  STRATEGIC_REACTIVATION: { label: "Reactivation", color: "bg-purple-500", textColor: "text-purple-400", icon: RotateCcw },
  CLAIMED: { label: "Claimed", color: "bg-cyan-500", textColor: "text-cyan-400", icon: User },
  OUTREACH_DRAFTED: { label: "Outreach Drafted", color: "bg-indigo-500", textColor: "text-indigo-400", icon: ArrowRight },
  CONTACTED: { label: "Contacted", color: "bg-amber-500", textColor: "text-amber-400", icon: ArrowRight },
  MEETING_SCHEDULED: { label: "Meeting", color: "bg-orange-500", textColor: "text-orange-400", icon: Clock },
  PROPOSAL_SENT: { label: "Proposal Sent", color: "bg-pink-500", textColor: "text-pink-400", icon: ArrowRight },
  WON: { label: "Won", color: "bg-green-500", textColor: "text-green-400", icon: TrendingUp },
  DORMANT: { label: "Dormant", color: "bg-slate-600", textColor: "text-slate-600", icon: Clock },
};

const TYPE_BADGE = {
  OPPORTUNITY: "bg-red-500/15 text-red-400 border-red-500/20",
  REACTIVATION: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  PIPELINE: "bg-blue-500/15 text-blue-400 border-blue-500/20",
};

function BoardCard({ card, columns, onMove, onClaim }) {
  const typeBadge = TYPE_BADGE[card.type] || TYPE_BADGE.PIPELINE;
  const isNew = card.column === "NEW_SIGNALS" && !card.owner_id;

  // Calculate days in column
  const daysInColumn = card.column_entered_at
    ? Math.round((Date.now() - new Date(card.column_entered_at).getTime()) / 86400000)
    : 0;

  return (
    <div
      data-testid={`board-card-${card.card_id}`}
      className="bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-600/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <Badge variant="outline" className={`${typeBadge} text-[10px] px-1.5 py-0`}>
          {card.type}
        </Badge>
        {card.priority_score > 0 && (
          <span className="text-[10px] font-mono text-slate-500">P:{card.priority_score}</span>
        )}
      </div>

      <h4 className="text-xs font-medium text-slate-900 mb-1.5 leading-snug line-clamp-2">{card.title}</h4>

      {card.context && (
        <p className="text-[10px] text-slate-500 mb-2 line-clamp-2">{card.context}</p>
      )}

      <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-2">
        {card.owner_id && (
          <span className="flex items-center gap-0.5">
            <User className="w-2.5 h-2.5" /> {card.owner_id.slice(-8)}
          </span>
        )}
        {daysInColumn > 0 && (
          <span className={`flex items-center gap-0.5 ${daysInColumn > 3 ? "text-amber-400" : ""}`}>
            <Clock className="w-2.5 h-2.5" /> {daysInColumn}d
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 pt-1.5 border-t border-slate-200">
        {isNew && (
          <Button
            data-testid={`claim-btn-${card.card_id}`}
            size="sm"
            variant="ghost"
            className="text-[10px] text-cyan-400 hover:bg-cyan-500/10 h-6 px-2"
            onClick={() => onClaim(card.card_id)}
          >
            Claim
          </Button>
        )}
        <Select onValueChange={(col) => onMove(card.card_id, col)}>
          <SelectTrigger data-testid={`move-select-${card.card_id}`} className="h-6 text-[10px] bg-slate-50 border-slate-200 flex-1">
            <SelectValue placeholder="Move to..." />
          </SelectTrigger>
          <SelectContent>
            {columns
              .filter((col) => col !== card.column)
              .map((col) => (
                <SelectItem key={col} value={col} className="text-xs">
                  {COLUMN_CONFIG[col]?.label || col}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function BoardColumn({ name, cards, columns, onMove, onClaim }) {
  const config = COLUMN_CONFIG[name] || { label: name, color: "bg-slate-500", textColor: "text-slate-600" };
  const Icon = config.icon || Kanban;

  return (
    <div data-testid={`column-${name}`} className="flex-shrink-0 w-64">
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className={`w-2 h-2 rounded-full ${config.color}`} />
        <span className={`text-xs font-medium ${config.textColor}`}>{config.label}</span>
        <Badge variant="outline" className="bg-white text-slate-600 border-slate-200 text-[10px] ml-auto">
          {cards.length}
        </Badge>
      </div>
      <div className="space-y-2 min-h-[200px]">
        {cards.map((card) => (
          <BoardCard
            key={card.card_id}
            card={card}
            columns={columns}
            onMove={onMove}
            onClaim={onClaim}
          />
        ))}
        {cards.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-xs">No cards</div>
        )}
      </div>
    </div>
  );
}

export default function ExecutionBoard() {
  const [board, setBoard] = useState({});
  const [columns, setColumns] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOwner, setFilterOwner] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterOwner !== "all") params.set("owner_id", filterOwner);

      const [boardRes, escalationsRes] = await Promise.all([
        fetch(`${API}/execution-board?${params}`, { credentials: "include" }),
        fetch(`${API}/execution-board/escalations`, { credentials: "include" }),
      ]);

      if (boardRes.ok) {
        const data = await boardRes.json();
        setBoard(data.board || {});
        setColumns(data.columns || []);
        setTotalCards(data.total_cards || 0);
      }
      if (escalationsRes.ok) {
        const data = await escalationsRes.json();
        setEscalations(data.escalations || []);
      }
    } catch (err) {
      console.error("Failed to fetch board:", err);
    } finally {
      setLoading(false);
    }
  }, [filterOwner]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMove = async (cardId, targetColumn) => {
    try {
      const res = await fetch(`${API}/execution-board/cards/${cardId}/move`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ column: targetColumn }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Moved to ${COLUMN_CONFIG[targetColumn]?.label || targetColumn}`);
        fetchData();
      } else {
        toast.error(data.detail || "Cannot move card");
      }
    } catch { toast.error("Failed to move card"); }
  };

  const handleClaim = async (cardId) => {
    try {
      const res = await fetch(`${API}/execution-board/cards/${cardId}/claim`, {
        method: "PUT",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Card claimed");
        fetchData();
      }
    } catch { toast.error("Failed to claim card"); }
  };

  if (loading) {
    return <div className="text-center py-16 text-slate-500">Loading board...</div>;
  }

  return (
    <div data-testid="execution-board-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Kanban className="w-6 h-6 text-cyan-400" />
            Commercial Execution Board
          </h1>
          <p className="text-sm text-slate-600 mt-1">{totalCards} active cards across {columns.length} columns</p>
        </div>
        <div className="flex items-center gap-2">
          {escalations.length > 0 && (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
              <AlertTriangle className="w-3 h-3 mr-1" /> {escalations.length} escalations
            </Badge>
          )}
          <Button
            data-testid="refresh-board-btn"
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-slate-200 text-slate-300"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Escalation Banner */}
      {escalations.length > 0 && (
        <div data-testid="escalation-banner" className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs text-amber-400 font-medium mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Escalations requiring attention
          </div>
          <div className="grid gap-1">
            {escalations.slice(0, 5).map((e, i) => (
              <div key={i} className="text-xs text-slate-600 flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${e.severity === "high" ? "bg-red-500" : "bg-amber-500"}`} />
                <span className="text-slate-300">{e.title?.slice(0, 60)}</span>
                <span className="text-slate-600">|</span>
                <span>{e.type?.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Board */}
      <div data-testid="board-columns" className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: "400px" }}>
        {columns.map((col) => (
          <BoardColumn
            key={col}
            name={col}
            cards={board[col] || []}
            columns={columns}
            onMove={handleMove}
            onClaim={handleClaim}
          />
        ))}
      </div>
    </div>
  );
}
